import { z } from "zod";
import { noteRowSchema } from "./note/crud";

export const callSchema = z.object({
  id: z.guid(),
  service_unique_id: z.string(),
  service_internal_id: z.string().nullable(),
  source: z.string(),
  status: z.enum(["busy", "no_answer", "answered", "missed"]).nullable(),
  caller_phone_number: z.string().nullable(),
  receiver_phone_number: z.string().nullable(),
  direction: z.enum(["outgoing", "incoming"]).nullable(),
  ongoing: z.boolean(),
  duration_seconds: z.number().nullable(),
  transcription_available_at: z.string().nullable(),
  summary_available_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  tags: z.array(z.string()),
});
export type CallSchema = z.infer<typeof callSchema>;

export const talentCallTypeSchema = z.enum([
  "qualification",
  "mission_proposal",
  "mission_follow_up",
  "timesheet_reminder",
  "talent_support",
  "whats_up",
  "other",
]);
export type TalentCallType = z.infer<typeof talentCallTypeSchema>;

export const updateCallPayloadSchema = z.object({
  tags: z.array(z.string()).optional(),
});
export type UpdateCallPayload = z.infer<typeof updateCallPayloadSchema>;

export const callTranscriptionSegmentSchema = z.object({
  channel: z.number(),
  startSec: z.number(),
  endSec: z.number(),
  text: z.string(),
});
export type CallTranscriptionSegmentSchema = z.infer<typeof callTranscriptionSegmentSchema>;

export const callTranscriptionSchema = z.object({
  speechState: z.string(),
  segments: z.array(callTranscriptionSegmentSchema),
});
export type CallTranscriptionSchema = z.infer<typeof callTranscriptionSchema>;

export const callSummarySchema = z.object({
  summary: z.string().nullable(),
  summaryState: z.string(),
  baseInstructions: z.string().nullable(),
});
export type CallSummarySchema = z.infer<typeof callSummarySchema>;

export const patchCallSummaryInstructionsPayloadSchema = z.object({
  baseInstructions: z.string().min(1),
});
export type PatchCallSummaryInstructionsPayload = z.infer<typeof patchCallSummaryInstructionsPayloadSchema>;

export const callSummarySettingsSchema = z.object({
  baseInstructions: z.string().nullable(),
});
export type CallSummarySettingsSchema = z.infer<typeof callSummarySettingsSchema>;

export const callConsultantDisplaySchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
});
export type CallConsultantDisplay = z.infer<typeof callConsultantDisplaySchema>;

export const ongoingCallSchema = z.object({
  hasOngoingCall: z.boolean(),
  call: callSchema.nullable(),
  consultant: callConsultantDisplaySchema.nullable(),
});
export type OngoingCall = z.infer<typeof ongoingCallSchema>;

export const ongoingCallTalentSchema = z.object({
  hasOngoingCall: z.boolean(),
  talent: z
    .object({
      id: z.guid(),
      first_name: z.string().nullable(),
      last_name: z.string().nullable(),
    })
    .nullable(),
});
export type OngoingCallTalent = z.infer<typeof ongoingCallTalentSchema>;

export const callLinkedNoteResponseSchema = z.object({
  note: noteRowSchema.nullable(),
});
export type CallLinkedNoteResponse = z.infer<typeof callLinkedNoteResponseSchema>;

const consultantDisplaySchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
});
export const activeCallInteractionSchema = z.object({
  call_id: z.guid(),
  direction: z.enum(["outgoing", "incoming"]),
  is_ongoing: z.boolean(),
  consultant: consultantDisplaySchema,
});
export type ActiveCallInteraction = z.infer<typeof activeCallInteractionSchema>;

// Outside production only: simulated SipSim calls
export const startSimulatedSipsimCallBodySchema = z.object({
  talentId: z.guid(),
});
export type StartSimulatedSipsimCallBody = z.infer<typeof startSimulatedSipsimCallBodySchema>;

export const simulatedSipsimCallParamsSchema = z.object({
  pbxId: z.string().regex(/^\d+$/),
});
export type SimulatedSipsimCallParams = z.infer<typeof simulatedSipsimCallParamsSchema>;

export const simulatedSipsimCallScenarioSchema = z.enum([
  "inaudible",
  "qualification",
  "mission_proposal",
  "mission_follow_up",
  "timesheet_reminder",
  "talent_support",
  "whats_up",
  "qualification_and_mission_proposal",
]);
export type SimulatedSipsimCallScenario = z.infer<typeof simulatedSipsimCallScenarioSchema>;

export const endSimulatedSipsimCallBodySchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("answered"), scenario: simulatedSipsimCallScenarioSchema }),
  z.object({ status: z.literal("no_answer") }),
]);
export type EndSimulatedSipsimCallBody = z.infer<typeof endSimulatedSipsimCallBodySchema>;

export const simulatedSipsimCallSchema = z.object({
  pbxId: z.string(),
});
export type SimulatedSipsimCall = z.infer<typeof simulatedSipsimCallSchema>;
